<template>
  <div v-if="viewModel && viewModel.hasData">
    <h2 class="best-offers__header">
      {{ $res.Views.BestTourPromotions }}
    </h2>
    <div class="best-offers__filters">
      <single-select
          class="custom-select--prom-country"
          :items="destinationCountryIds"
          v-model="selectedCountryId"
          :item-id-fn="x => x"
          :item-text-fn="x => getDestinationCountryName(x)"
          dropdown-width-policy="eq"
      >
        <template v-slot:selected-item>
          <div class="d-flex justify-content-between align-items-center">
            <div class="best-offers__country-item">
              <img v-if="!!optionSelectedCountry"
                   :src="destinationOptionIcon(selectedCountryId)"
                   class="best-offers__header__icon"
              />
              <span>{{ getDestinationCountryName(selectedCountryId) }}</span>
            </div>
          </div>
        </template>
        <template v-slot:element="{item}">
          <div class="d-flex justify-content-between align-items-center">
            <div class="best-offers__country-item">
              <img v-if="!!getDestinationCountryIso(item)"
                   :src="destinationOptionIcon(item)"
                   class="best-offers__header__icon"
              />
              <span>{{ getDestinationCountryName(item) }}</span>
            </div>
          </div>
        </template>
      </single-select>
      <best-offers-pagination v-model="currentPage"
                              :total-pages="totalPages"
      />
    </div>
    <tabs-component :items="packageTourPromotionTypes"
                    v-model="selectedTypeId"
                    class="best-offers__tabs"
    />
    <best-offers-slider style="margin-top: 10px;" 
                        :items="filteredPackageTourPromotions"
                        :current-page="currentPage"
    >
      <template v-slot:default="{item}">
        <offer-item :view-model="item"
                    :country="getCountry(item.hotelCountryId)"
                    :location="getLocation(item.hotelLocationId)"
                    :currency="getCurrency(item.currencyId)"
                    :is-authorized="viewModel.isAuthorized"
        ></offer-item>
      </template>
    </best-offers-slider>
  </div>
</template>

<script lang="ts">
import {post} from "@/common/fetch";
import BestOffersSlider from "./BestOffersSliderComponent.vue";
import BestOffersPagination from "./BestOffersPagintationComponent.vue"
import OfferItem from "./OfferItemComponentDesktop.vue";
import TabsComponent from "@/common/controls/TabsComponentDesktop.vue";
import SingleSelect from "@/common/controls/forms/single-select/SingleSelect.vue";
import SvgIcon from "@/common/controls/SvgIconControl.vue";

export default {
  components: {
    BestOffersSlider,
    BestOffersPagination,
    OfferItem,
    TabsComponent,
    SingleSelect,
    SvgIcon,
  },
  data() {
    return {
      viewModel: {},
      selectedCountryId: -1,
      selectedTypeId: this.viewModel?.packageTourPromotionTypes?.[0]?.id,
      currentPage: 0,
      initialDestinationCountryIds: [],
      initialDestinationCountries: [],
    }
  },
  computed: {
    destinationCountryIds() {
      return [-1, ...this.initialDestinationCountryIds]
    },
    departureLocation() {
      return this.viewModel.departureLocation;
    },
    optionSelectedCountry() {
      if (this.selectedCountryId === -1) {
        return null;
      }
      
      return this.getCountry(this.selectedCountryId);
    },
    packageTourPromotionTypes() {
      const types = this.viewModel?.packageTourPromotionTypes ?? [];
      return types.map(promoType => ({
        value: promoType.id,
        text: promoType.name,
      }))
    },
    filteredPackageTourPromotions() {
      const promotions = this.viewModel?.packageTourPromotions ?? [];
      return promotions.filter(p => p.packageTourPromotionTypeId === this.selectedTypeId);
    },
    totalPages() {
      const total = Math.ceil(this.filteredPackageTourPromotions.length / 6);
      return Number.isFinite(total) && total > 0 ? total : 1;
    },
  },
  watch: {
    selectedTypeId() {
      this.currentPage = 0;
    },
    selectedCountryId() {
      this.fetchPromotions();
    },
  },
  mounted() {
    this.fetchPromotions();
    document.addEventListener('tour-operator-office:change', this.fetchPromotions);
  },
  beforeDestroy() {
    document.removeEventListener('tour-operator-office:change', this.fetchPromotions);
  },
  methods: {
    fetchPromotions() {
      const countryId = this.selectedCountryId > 0 ? this.selectedCountryId : null;
      post('/Home/GetPackageTourPromotions', { countryId: countryId }, true)
        .then(response => this.updateViewModel(countryId, response));
    },
    updateViewModel(countryId, response) {
      this.viewModel = response;
      if (response && response.hasData) {
        if (countryId === null) {
          this.initialDestinationCountryIds = this.viewModel.destinationCountryIds;
          this.initialDestinationCountries = this.viewModel.countries;
        }
        const { packageTourPromotionTypes: tabs } = this.viewModel;
        const currentSelectedTabExists = tabs.some(x => x.id === this.selectedTypeId);

        if (!currentSelectedTabExists) {
          this.selectedTypeId = tabs?.[0]?.id ?? null;
        }
        this.currentPage = 0;
      }
    },
    getCountry(id) {
      return this.viewModel?.countries?.find(x => x.id === id);
    },
    
    getDestinationCountry(id) {
      return this.initialDestinationCountries.find(x => x.id === id);
    },

    getLocation(id) {
      return this.viewModel?.locations?.find(x => x.id === id);
    },

    getCurrency(id) {
      return this.viewModel?.currencies?.find(x => x.id === id);
    },

    destinationOptionIcon(option) {
      if (option === -1) {
        return null;
      }

      const country = this.getDestinationCountry(option);
      if(country && country.codeAlpha2) {
        return `/Content/Images/FlagIcons/${country.codeAlpha2}.svg`;
      }
    },
    
    getDestinationCountryName(id) {
      if (id === -1) {
        return this.$res.General.AllCountries;
      }

      const country = this.initialDestinationCountries.find(x => x.id === id);
      return country ? country.name : "";
    },
    
    getCountryName(id) {
      if (id === -1) {
        return this.$res.General.AllCountries;
      }
      
      const country = this.getCountry(id);
      return country ? country.name : "";
    },

    getDestinationCountryIso(id) {
      if (id === -1) {
        return null;
      }

      const country = this.getDestinationCountry(id);
      return country ? country.codeAlpha2 : null;
    },

    onClear() {
      this.selectedCountryId = -1;
    },
  },
}
</script>

<style lang="scss" scoped>


.best-offers__header {
  padding-block-end: 12px;
  font-weight: 600;
  font-size: 28px;
  line-height: 30px;
}

.best-offers__tabs {
  margin-block-start: 20px;
  display: flex;
  flex-wrap: wrap;
  row-gap: 5px;
}

.best-offers-slider {
  width: 840px;
}

.best-offers__filters {
  display: flex;
  justify-content: space-between;
}

.best-offers__country-item {
  display: flex;
  gap: 8px;
  align-items: center;
  line-height: 20px;
}

.best-offers__header__icon {
  height: 15px;
  width: auto;
}

.best-offers__selected-marker {
  color: var(--main-30);
  
  [theme="hb"] & {
    color: var(--green-1);
  }
}

.best-offers__icon--close {
  position: relative;
  bottom: 2px;
  color: var(--second-30);
}

.best-offers__icon--close:hover {
  color: var(--second-10);
}

:deep(.custom-select--prom-country) {
  width: 300px;
  max-width: 300px;
  flex: 0 0 300px;
}

:deep(.tabs-component > .tabs-component__link) {
  font-size: 13px;
  line-height: 20px;
  font-weight: 400;
  color: var(--main-10);
  box-shadow: var(--shadow);
  background-color: var(--white);
  border-radius: var(--border-radius-md);
  padding: 5px 16px;
  border: none;
  margin-inline-end: 8px;
}

:deep(.tabs-component > .tabs-component__link:not(.tabs-component__link_active):hover) {
   box-shadow: var(--shadow-hover)
}

:deep(.tabs-component > .tabs-component__link_active) {
  box-shadow: none;
  background-color: var(--main-10);
  color: var(--white);
}

:deep(.custom-select--prom-country .custom-select__icon) {
  display: none;
}

:deep(.custom-select--prom-country .country-select__selected-icon) {
  height: 12px;
  width: auto;
  margin-inline-end: 0.5rem;
}
</style>


